import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsAPI } from '@matix/api-client';
import {
  ReportToolbar, Field, fmt3, fmtMoney, todayTo, splitLocal, useLabelOf, printableHtml,
} from './reportShared';
import { printReport, type ReportPaper } from './reportPrint';
import { ReportSheetBuilder, downloadReportExcel } from './reportExcel';
import { useLang } from '../../lib/language';

type Params = {
  store_id?: number; from?: string; to?: string;
  from_time?: string; to_time?: string; q?: string; include_zero?: number;
};

/**
 * Items Stock / مخزون الاصناف — every item's balance per warehouse, as of a
 * date.
 *
 * The warehouse list is the user's own scope (the same allow-list the Stock
 * Balance lookup honours) and defaults to the till's branch store; "All"
 * prints one section per warehouse. Both ends are optional, and each carries
 * a time of day as well as a date (2026-09-15) — a warehouse counted at 14:00
 * is a different balance from the same warehouse at closing. "To" is the
 * moment the balance is taken at (cleared = live). "From" narrows the list to
 * items that MOVED between the two — the user's chosen meaning for a
 * date range on a balance-only report (2026-09-08); cleared, every item with
 * stock is listed. The server still computes opening / in / out around the
 * dates; the screen shows only the balance (the movement columns
 * were dropped on 2026-09-08 at the user's request). Items with no balance
 * are hidden unless asked for, so a catalogue of two thousand items does not
 * print two thousand zero rows. Unit price is the selling price of the unit
 * shown and Total = balance × that price (2026-10-06), summed per warehouse.
 */
export function ItemsStockReport(): JSX.Element {
  const { t, lang } = useLang();
  const labelOf = useLabelOf();
  const printRef = useRef<HTMLDivElement>(null);
  const [storeId, setStoreId] = useState<number | ''>('');
  const [storeTouched, setStoreTouched] = useState(false);
  const [from, setFrom] = useState('');
  const [asOf, setAsOf] = useState(todayTo);
  const [q, setQ] = useState('');
  const [includeZero, setIncludeZero] = useState(false);
  const [params, setParams] = useState<Params | null>(null);

  const storesQ = useQuery({ queryKey: ['rpt-stock-stores'], queryFn: () => reportsAPI.itemsStockStores() });
  useEffect(() => {
    if (storesQ.data && !storeTouched && storeId === '' && storesQ.data.default_store_id != null) {
      setStoreId(storesQ.data.default_store_id);
    }
  }, [storesQ.data, storeTouched, storeId]);

  const dataQ = useQuery({
    queryKey: ['items-stock', params],
    queryFn: () => reportsAPI.itemsStock(params!),
    enabled: params != null,
  });

  // Both ends are optional here and each carries its own time of day, so the
  // pair is split one end at a time rather than through `rangeParams`.
  const movedFrom = splitLocal(from);
  const asOfPoint = splitLocal(asOf);

  function run(): void {
    setParams({
      ...(storeId !== '' ? { store_id: storeId } : {}),
      ...(movedFrom.date ? { from: movedFrom.date } : {}),
      ...(movedFrom.date && movedFrom.time ? { from_time: movedFrom.time } : {}),
      ...(asOfPoint.date ? { to: asOfPoint.date } : {}),
      ...(asOfPoint.date && asOfPoint.time ? { to_time: asOfPoint.time } : {}),
      ...(q.trim() ? { q: q.trim() } : {}),
      ...(includeZero ? { include_zero: 1 } : {}),
    });
  }

  const stamp = (v: { date: string; time: string | null }) => `${v.date}${v.time ? ` ${v.time}` : ''}`;
  const toLabel = asOfPoint.date
    ? `${t({ en: 'As of', ar: 'حتى تاريخ' })} ${stamp(asOfPoint)}`
    : t({ en: 'Live balance', ar: 'الرصيد الحالي' });
  const dateLabel = movedFrom.date
    ? `${t({ en: 'Moved from', ar: 'حركة من' })} ${stamp(movedFrom)} · ${toLabel}`
    : toLabel;
  const title = t({ en: 'Items Stock Report', ar: 'تقرير مخزون الاصناف' });

  function exportPdf(paper: ReportPaper): void {
    printReport(title, dateLabel, printableHtml(printRef), paper);
  }

  const d = dataQ.data;
  const store = storesQ.data?.stores.find((s) => s.id === storeId);
  const scopeLabel = store
    ? labelOf(store)
    : t({ en: 'All warehouses', ar: 'كل المخازن' });

  function exportExcel(): void {
    if (!d) return;
    const b = new ReportSheetBuilder().title(title, `${scopeLabel} · ${dateLabel}`);
    const cols = [
      { label: t({ en: 'Code', ar: 'الكود' }) },
      { label: t({ en: 'Item', ar: 'الصنف' }) },
      { label: t({ en: 'Unit', ar: 'الوحدة' }) },
      { label: t({ en: 'Balance', ar: 'الرصيد' }), kind: 'qty' as const },
      { label: t({ en: 'Unit price', ar: 'سعر الوحدة' }), kind: 'money' as const },
      { label: t({ en: 'Total', ar: 'الإجمالي' }), kind: 'money' as const },
    ];
    for (const g of d.stores) {
      const name = labelOf({ name: g.store_name, name_ar: g.store_name_ar });
      b.section(g.store_code ? `${name} (${g.store_code})` : name)
        .table(
          cols,
          g.items.map((it) => [
            it.code,
            labelOf({ name: it.name, name_ar: it.name_ar }),
            it.unit || it.unit_ar ? labelOf({ name: it.unit ?? '', name_ar: it.unit_ar }) : '',
            it.closing,
            it.unit_price ?? 0,
            it.total_value ?? 0,
          ]),
          {
            emptyText: t({ en: 'No stock for these filters.', ar: 'لا يوجد مخزون لهذه المرشحات.' }),
            totals: [
              `${t({ en: 'Total', ar: 'الإجمالي' })} (${g.items.length.toLocaleString()})`,
              null, null, g.totals.closing, null, g.totals.total_value ?? 0,
            ],
          },
        );
    }
    if (d.stores.length > 1) {
      b.table(cols, [], {
        header: false,
        totals: [t({ en: 'Grand Total', ar: 'الإجمالي الكلي' }), null, null, d.totals.closing, null, d.totals.total_value ?? 0],
      });
    }
    downloadReportExcel(`${title} ${dateLabel}`, b.build(title, { rightToLeft: lang === 'ar' }));
  }

  return (
    <div className="rpt-wrap">
      <ReportToolbar
        running={dataQ.isFetching}
        canExport={!!d}
        onRun={run}
        onExport={exportPdf}
        onExportExcel={exportExcel}
        filters={
          <>
            <Field label={t({ en: 'Warehouse', ar: 'المخزن' })}>
              <select
                value={storeId}
                onChange={(e) => { setStoreTouched(true); setStoreId(e.target.value ? Number(e.target.value) : ''); }}
              >
                <option value="">{t({ en: '— All —', ar: '— الكل —' })}</option>
                {storesQ.data?.stores.map((s) => <option key={s.id} value={s.id}>{labelOf(s)}</option>)}
              </select>
            </Field>
            <Field label={t({ en: 'From', ar: 'من' })}>
              <input type="datetime-local" step={60} value={from} max={asOf || undefined} onChange={(e) => setFrom(e.target.value)} />
            </Field>
            <Field label={t({ en: 'To', ar: 'إلى' })}>
              <input type="datetime-local" step={60} value={asOf} min={from || undefined} onChange={(e) => setAsOf(e.target.value)} />
            </Field>
            <Field label={t({ en: 'Item (code or name)', ar: 'الصنف (كود أو اسم)' })}>
              <input
                type="text"
                value={q}
                placeholder={t({ en: 'All items', ar: 'كل الأصناف' })}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') run(); }}
              />
            </Field>
            <label className="rpt-field rpt-field-check">
              <span className="rpt-field-label">{t({ en: 'Zero rows', ar: 'الأصناف الصفرية' })}</span>
              <span className="rpt-check">
                <input type="checkbox" checked={includeZero} onChange={(e) => setIncludeZero(e.target.checked)} />
                {t({ en: 'Show items with no stock', ar: 'إظهار الأصناف بدون رصيد' })}
              </span>
            </label>
          </>
        }
      />

      {dataQ.error && <div className="rpt-error">{t({ en: 'Failed to load report. Try again.', ar: 'تعذّر تحميل التقرير. حاول مجدداً.' })}</div>}

      <div className="rpt-print" ref={printRef}>
        {d && (
          <>
            <div className="rpt-meta">{scopeLabel} · {dateLabel}</div>
            {d.truncated && (
              <div className="rpt-error">
                {t({
                  en: `Showing the first ${d.row_count.toLocaleString()} rows — narrow the filters to see the rest.`,
                  ar: `يتم عرض أول ${d.row_count.toLocaleString()} صف — ضيّق المرشحات لعرض الباقي.`,
                })}
              </div>
            )}
            {d.stores.length === 0 && <div className="rpt-empty">{t({ en: 'No stock for these filters.', ar: 'لا يوجد مخزون لهذه المرشحات.' })}</div>}
            {d.stores.map((g) => (
              <div key={g.store_id}>
                <h2 className="section">
                  {labelOf({ name: g.store_name, name_ar: g.store_name_ar })}
                  {g.store_code ? ` (${g.store_code})` : ''}
                </h2>
                <table>
                  <thead>
                    <tr>
                      <th>{t({ en: 'Code', ar: 'الكود' })}</th>
                      <th>{t({ en: 'Item', ar: 'الصنف' })}</th>
                      <th>{t({ en: 'Unit', ar: 'الوحدة' })}</th>
                      <th className="num">{t({ en: 'Balance', ar: 'الرصيد' })}</th>
                      <th className="num">{t({ en: 'Unit price', ar: 'سعر الوحدة' })}</th>
                      <th className="num">{t({ en: 'Total', ar: 'الإجمالي' })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.items.map((it) => (
                      <tr key={it.item_id}>
                        <td>{it.code}</td>
                        <td>{labelOf({ name: it.name, name_ar: it.name_ar })}</td>
                        <td>{it.unit || it.unit_ar ? labelOf({ name: it.unit ?? '', name_ar: it.unit_ar }) : ''}</td>
                        <td className="num">{fmt3(it.closing)}</td>
                        <td className="num">{fmtMoney(it.unit_price)}</td>
                        <td className="num">{fmtMoney(it.total_value)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={3}>{t({ en: 'Total', ar: 'الإجمالي' })} ({g.items.length.toLocaleString()})</td>
                      <td className="num">{fmt3(g.totals.closing)}</td>
                      <td />
                      <td className="num">{fmtMoney(g.totals.total_value)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ))}
            {d.stores.length > 1 && (
              <table style={{ marginTop: 8 }}>
                <tfoot>
                  <tr className="total-row">
                    <td>{t({ en: 'Grand Total', ar: 'الإجمالي الكلي' })}</td>
                    <td className="num">{fmt3(d.totals.closing)}</td>
                    <td className="num">{fmtMoney(d.totals.total_value)}</td>
                  </tr>
                </tfoot>
              </table>
            )}
          </>
        )}
        {!d && !dataQ.isFetching && <div className="rpt-empty">{t({ en: 'Choose a warehouse and a date and time, then “Show report”.', ar: 'اختر المخزن والتاريخ والوقت، ثم “عرض التقرير”.' })}</div>}
      </div>
    </div>
  );
}
